import clsx from "clsx";
import { FC, Suspense, useEffect } from "react";
import { useFragment } from "react-relay";
import {
  i18n,
  Toggle,
  useLocalStorage,
  useQueryParam,
  useWindowDimensions,
} from "rujira.ui";
import { AngleDown } from "rujira.ui/src/components/icons/Icons";
import { usePreloadedAccountData } from "../../services/accountData";
import {
  TradeSubscriptionsFragment$data,
  TradeSubscriptionsFragment$key,
} from "../__generated__/TradeSubscriptionsFragment.graphql";
import { fragment } from "../TradeSubscriptions";
import { OrderType } from "../types";
import { AutoClaimerStatusMenu } from "./AutoClaimerStatusMenu";
import { useCalcMetadata } from "./Calc";
import { useTradeContext } from "./Context";
import {
  LimitOrdersHistory,
  LimitOrdersTable,
  LimitOrdersTableFallback,
} from "./LimitOrders";
import { RecurringOrdersTable } from "./RecurringOrders";

const OrdersBreakpoint = 1350;

export const Orders: FC = () => {
  return (
    <Suspense fallback={<OrdersFallback />}>
      <OrdersInner />
    </Suspense>
  );
};

export const OrdersFallback: FC = () => {
  const isCondensed = useWindowDimensions(OrdersBreakpoint);
  const metadata = useCalcMetadata();

  const [tab] = useQueryParam<
    | "limit-open"
    | "limit-filled"
    | "limit-history"
    | "recurring-active"
    | "recurring-paused"
    | "recurring-completed"
  >("orders", "limit-open");

  return (
    <>
      <div className="flex ai-c gap-2">
        <nav className="tabs tabs--sm">
          {!!metadata && (
            <>
              <a className="selected w-16 flex jc-sb">
                <i18n.p className="color-white ml-1">
                  {i18n.t(tab.startsWith("limit") ? "Limit" : "Recurring")}
                </i18n.p>
                <AngleDown className="color-white" />
                <nav>
                  {!tab.startsWith("limit") && (
                    <span
                      className="flex ai-c"
                      style={{
                        fontSize: "0.75rem",
                      }}>
                      <i18n.label className="ml-1">Limit</i18n.label>
                    </span>
                  )}
                  {!tab.startsWith("recurring") && (
                    <span
                      className="flex ai-c"
                      style={{
                        fontSize: "0.75rem",
                      }}>
                      <i18n.label className="ml-1">Recurring</i18n.label>
                    </span>
                  )}
                </nav>
              </a>
              <div className="divider mx-1" />
            </>
          )}
          {tab.startsWith("limit") ? (
            isCondensed ? (
              <a className="w-16 flex jc-sb">
                <i18n.p className="color-white nowrap">{i18n.t("Open")}</i18n.p>
                <AngleDown className="color-white" />
              </a>
            ) : (
              <>
                <a>{i18n.t("Open")}</a>
                <a>{i18n.t("Filled")}</a>
                <a>{i18n.t("History")}</a>
              </>
            )
          ) : isCondensed ? (
            <a className="w-16 flex jc-sb">
              <i18n.p className="color-white nowrap">{i18n.t("Active")}</i18n.p>
              <AngleDown className="color-white" />
            </a>
          ) : (
            <>
              <a>{i18n.t("Active")}</a>
              <a>{i18n.t("Paused")}</a>
              <a>{i18n.t("Completed")}</a>
            </>
          )}
        </nav>
      </div>
      <LimitOrdersTableFallback />
    </>
  );
};

export const OrdersInner: FC = () => {
  const { address } = useTradeContext();
  const isCondensed = useWindowDimensions(OrdersBreakpoint);

  const { accountData } = usePreloadedAccountData();
  const metadata = useCalcMetadata();
  const d = useFragment<TradeSubscriptionsFragment$key>(fragment, accountData);

  const finOrders = (d?.fin?.ordersV2?.edges
    ?.filter((o) => o?.node?.__typename === "FinOrder")
    .map((o) => o?.node) || []) as Array<FinOrderNode>;

  const [only, setOnly] = useLocalStorage("trade-only", false);

  const [tab, setTab] = useQueryParam<
    | "limit-open"
    | "limit-filled"
    | "limit-history"
    | "recurring-active"
    | "recurring-paused"
    | "recurring-completed"
  >("orders", "limit-open");

  const [type] = useQueryParam<OrderType>("order", OrderType.Market);

  useEffect(() => {
    setTab(type === OrderType.Recurring ? "recurring-active" : "limit-open");
  }, [type]);

  const open =
    finOrders &&
    [...finOrders]
      .sort((a, b) => Number(a?.rate) - Number(b?.rate))
      .filter((o) =>
        address ? (only ? o?.pair.address === address : true) : false
      )
      .reduce(
        (agg: NonNullable<FinOrderNode>[], x) =>
          x?.remaining ? [x, ...agg] : agg,
        []
      );

  const filled =
    finOrders &&
    [...finOrders]
      .sort((a, b) => Number(a?.rate) - Number(b?.rate))
      .filter((o) =>
        address ? (only ? o?.pair.address === address : true) : false
      )
      .reduce(
        (agg: NonNullable<FinOrderNode>[], x) =>
          x?.filled ? [x, ...agg] : agg,
        []
      );

  const recurring =
    (d?.fin?.ordersV2?.edges
      ?.filter(
        (o) =>
          o?.node &&
          o.node.__typename === "CalcOrder" &&
          o.node.source === "rj" &&
          (!only ||
            o.node.config.nodes?.filter(
              (n) =>
                n.action?.routes?.filter((r) => r.pairAddress === address)
                  .length
            ).length)
      )
      .map((o) => o?.node) as Array<CalcOrderNode>) || [];

  const active = recurring.filter(
    ({ status, balances }) =>
      status === "ACTIVE" && balances?.some((b) => BigInt(b?.amount) > 10n)
  );

  const paused = recurring.filter(({ status }) => status === "PAUSED");

  const completed = recurring.filter(
    ({ status, balances }) =>
      status === "ACTIVE" && balances?.every((b) => BigInt(b?.amount) < 10n)
  );

  return (
    <>
      <div className="orders-header flex ai-c gap-2">
        <nav className="tabs tabs--sm">
          {!!metadata && (
            <>
              <a className="selected w-16 flex jc-sb">
                <i18n.p className="color-white ml-1">
                  {i18n.t(tab.startsWith("limit") ? "Limit" : "Recurring")}
                </i18n.p>
                <AngleDown className="color-white" />
                <nav>
                  {!tab.startsWith("limit") && (
                    <span
                      className="flex ai-c"
                      style={{
                        fontSize: "0.75rem",
                      }}
                      onClick={() => {
                        setTab("limit-open");
                      }}>
                      <i18n.label className="ml-1">Limit</i18n.label>
                      {open.length || filled.length ? (
                        <i>{open.length + filled.length || undefined}</i>
                      ) : null}
                    </span>
                  )}
                  {!tab.startsWith("recurring") && (
                    <span
                      className="flex ai-c"
                      style={{
                        fontSize: "0.75rem",
                      }}
                      onClick={() => {
                        setTab("recurring-active");
                      }}>
                      <i18n.label className="ml-1">Recurring</i18n.label>
                      {active.length ? (
                        <i>{active.length || undefined}</i>
                      ) : null}
                    </span>
                  )}
                </nav>
              </a>
              <div className="divider mx-1" />
            </>
          )}
          {tab.startsWith("limit") ? (
            isCondensed ? (
              <a className="w-16 flex jc-sb">
                <i18n.p className="color-white nowrap">
                  {i18n.t(
                    {
                      "limit-open": "Open",
                      "limit-filled": "Filled",
                      "limit-history": "History",
                      "recurring-active": "Active",
                      "recurring-paused": "Paused",
                      "recurring-completed": "Completed",
                    }[tab]
                  )}
                </i18n.p>
                <AngleDown className="color-white" />
                <nav>
                  <span
                    className="flex ai-c"
                    style={{
                      fontSize: "0.75rem",
                    }}
                    onClick={() => {
                      setTab("limit-open");
                    }}>
                    <i18n.label className="ml-1">Open</i18n.label>
                    {open.length ? <i>{open.length || undefined}</i> : null}
                  </span>
                  <span
                    className="flex ai-c"
                    style={{
                      fontSize: "0.75rem",
                    }}
                    onClick={() => {
                      setTab("limit-filled");
                    }}>
                    <i18n.label className="ml-1">Filled</i18n.label>
                    {filled.length ? <i>{filled.length || undefined}</i> : null}
                  </span>
                  <span
                    className="flex ai-c"
                    style={{
                      fontSize: "0.75rem",
                    }}
                    onClick={() => {
                      setTab("limit-history");
                    }}>
                    <i18n.label className="ml-1">History</i18n.label>
                  </span>
                </nav>
              </a>
            ) : (
              <>
                <a
                  className={tab === "limit-open" ? "selected" : ""}
                  onClick={() => setTab("limit-open")}>
                  {i18n.t("Open")}
                  {open && open.length ? <i>{open.length}</i> : null}
                </a>
                <a
                  className={tab === "limit-filled" ? "selected" : ""}
                  onClick={() => setTab("limit-filled")}>
                  {i18n.t("Filled")}
                  {filled && filled.length ? <i>{filled.length}</i> : null}
                </a>
                <i18n.a
                  className={tab === "limit-history" ? "selected" : ""}
                  onClick={() => setTab("limit-history")}>
                  {i18n.t("History")}
                </i18n.a>
              </>
            )
          ) : isCondensed ? (
            <a className="w-16 flex jc-sb">
              <i18n.p className="color-white">
                {i18n.t(
                  {
                    "limit-open": "Open",
                    "limit-filled": "Filled",
                    "limit-history": "History",
                    "recurring-active": "Active",
                    "recurring-paused": "Paused",
                    "recurring-completed": "Completed",
                  }[tab]
                )}
              </i18n.p>
              <AngleDown className="color-white" />
              <nav>
                <a
                  className="flex ai-c"
                  style={{
                    fontSize: "0.75rem",
                  }}
                  onClick={() => setTab("recurring-active")}>
                  {i18n.t("Active")}
                  {active.length ? <i>{active.length}</i> : null}
                </a>
                <a
                  className="flex ai-c"
                  style={{
                    fontSize: "0.75rem",
                  }}
                  onClick={() => setTab("recurring-paused")}>
                  {i18n.t("Paused")}
                  {paused.length ? <i>{paused.length}</i> : null}
                </a>
                <a
                  className="flex ai-c"
                  style={{
                    fontSize: "0.75rem",
                  }}
                  onClick={() => setTab("recurring-completed")}>
                  {i18n.t("Completed")}
                  {completed.length ? <i>{completed.length}</i> : null}
                </a>
              </nav>
            </a>
          ) : (
            <>
              <a
                className={tab === "recurring-active" ? "selected" : ""}
                onClick={() => setTab("recurring-active")}>
                {i18n.t("Active")}
                {active.length ? <i>{active.length}</i> : null}
              </a>
              <a
                className={tab === "recurring-paused" ? "selected" : ""}
                onClick={() => setTab("recurring-paused")}>
                {i18n.t("Paused")}
                {paused.length ? <i>{paused.length}</i> : null}
              </a>
              <a
                className={tab === "recurring-completed" ? "selected" : ""}
                onClick={() => setTab("recurring-completed")}>
                {i18n.t("Completed")}
                {completed.length ? <i>{completed.length}</i> : null}
              </a>
            </>
          )}
        </nav>

        <div className="orders-header__controls flex ai-c gap-0.5">
          <AutoClaimerStatusMenu />
          <Toggle
            className="toggle--xs as-c"
            checked={only}
            onChange={() => setOnly(!only)}
            label="Show this pair only"
          />
        </div>
      </div>
      {tab.startsWith("limit") ? (
        <>
          {tab === "limit-open" && (
            <LimitOrdersTable
              label="open"
              orders={open}
              contract={only ? address : undefined}
            />
          )}
          {tab === "limit-filled" && (
            <LimitOrdersTable
              label="filled"
              orders={filled}
              contract={only ? address : undefined}
            />
          )}
          {tab === "limit-history" && <LimitOrdersHistory />}
        </>
      ) : (
        <RecurringOrdersTable
          orders={
            tab === "recurring-active"
              ? active
              : tab === "recurring-paused"
                ? paused
                : completed
          }
          label={
            tab === "recurring-active"
              ? "active"
              : tab === "recurring-paused"
                ? "paused"
                : "completed"
          }
          hide={
            tab === "recurring-active"
              ? []
              : ["Time Remaining", "Next Execution"]
          }
        />
      )}
    </>
  );
};

export type OrderNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<TradeSubscriptionsFragment$data["fin"]>["ordersV2"]
    >["edges"]
  >[number]
>["node"];

type FinOrderNode = Extract<OrderNode, { __typename: "FinOrder" }>;
type CalcOrderNode = Extract<OrderNode, { __typename: "CalcOrder" }>;

enum OrderFilter {
  Limit = "limit",
  Recurring = "recurring",
}

export const blockSubscriptionId = btoa("ThorchainBlock:latest");

export const OrdersPortfolio: FC = () => {
  const { accountData } = usePreloadedAccountData();
  const d = useFragment<TradeSubscriptionsFragment$key>(fragment, accountData);
  const metadata = useCalcMetadata();

  const limitOrders = d?.fin?.ordersV2?.edges
    ?.filter((o) => o?.node?.__typename === "FinOrder")
    ?.map((o) => o?.node) as Array<FinOrderNode>;

  const recurringOrders = d?.fin?.ordersV2?.edges
    ?.filter(
      (o) =>
        o?.node?.__typename === "CalcOrder" &&
        o?.node.balances?.some((b) => BigInt(b?.amount) > 10n)
    )
    ?.map((o) => o?.node) as Array<CalcOrderNode>;

  const [filter, setFilter] = useQueryParam<OrderFilter>(
    "type",
    OrderFilter.Limit
  );

  const hasOrders = !!limitOrders.length || !!recurringOrders.length;

  return (
    <div className="px-1">
      {!!metadata && hasOrders && (
        <div className="tabs tabs--sm py-1">
          <a
            className={clsx({ selected: filter === OrderFilter.Limit })}
            onClick={() => setFilter(OrderFilter.Limit)}>
            <i18n.label>Limit</i18n.label>
            {limitOrders.length ? <i>{limitOrders.length}</i> : null}
          </a>
          <a
            className={clsx({ selected: filter === OrderFilter.Recurring })}
            onClick={() => setFilter(OrderFilter.Recurring)}>
            <i18n.label>Recurring</i18n.label>
            {recurringOrders.length ? <i>{recurringOrders.length}</i> : null}
          </a>
        </div>
      )}
      {filter === OrderFilter.Limit ? (
        <LimitOrdersTable orders={limitOrders} label="open" />
      ) : (
        <RecurringOrdersTable
          orders={recurringOrders}
          label="active"
          expanded
        />
      )}
    </div>
  );
};
